import { ApiResourceBaseClass as i } from "./baseApiCalls.js";
const a = "org";
class c extends i {
  /**
   * Searches for organizations based on the provided query parameters.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/search`
   *
   * @param {SearchOrgParams} queryParams - Parameters used to filter or refine the organization search.
   * @returns {Promise<FuzzySearchOrgsReceivedData>} A promise resolving to the data containing the search results.
   */
  searchOrgs = async (s) => {
    const { data: t } = await this.get(
      `${a}/search`,
      s
    );
    return t;
  };
  /**
   * Get organization details based on the provided organization ID.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId`
   *
   * @param {number} orgId - The ID of the organization to retrieve.
   * @returns {Promise<V2OrgData>} - The organization details.
   */
  getOrg = async (s) => {
    const { data: t } = await this.get(`${a}/${s}`);
    return t;
  };
  /**
   * Get the list of catagories of the organization businesses.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/categories`
   *
   * @param {number} orgId - The ID of the organization to retrieve.
   * @param {string} lang - The language to use for the category names.
   * @returns {Promise<OrgCategoriesResponse>} - The list of the organization categories.
   */
  getBusinessesCategory = async (s, t) => {
    const e = {
      lang: t
    }, { data: n } = await this.get(
      `${a}/${s}/categories`,
      e
    );
    return n;
  };
  /**
   * Get the list of countries of the organization businesses.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId`/countries
   *
   * @param {number} orgId - The ID of the organization to retrieve.
   * @returns {Promise<OrgCountriesResponse>} - The organization countries.
   */
  getBusinessesCountries = async (s) => {
    const { data: t } = await this.get(
      `${a}/${s}/countries`
    );
    return t;
  };
  /**
   * Get business edit fields for an organization.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/business_fields`
   *
   * @param {number} orgId - The ID of the organization to retrieve business fields for.
   * @returns {Promise<BusinessFieldsType>} A promise resolving to the business fields data.
   */
  getBusinessEditFields = async (s) => {
    const { data: t } = await this.get(
      `${a}/${s}/business_fields`
    );
    return t;
  };
  /**
   * Update business edit fields for an organization.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/org/:orgId/business_fields`
   *
   * @param {number} orgId - The ID of the organization to update business fields for.
   * @param {BusinessFieldsType} businessFields - The business fields data to update.
   * @returns {Promise<SuccessResponse>} A promise resolving to the success response.
   */
  updateBusinessEditFields = async (s, t) => {
    const { data: e } = await this.post(
      `${a}/${s}/business_fields`,
      t
    );
    return e;
  };
  /**
   * Get SSO information for an organization.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/sso_info`
   *
   * @param {number} orgId - The ID of the organization to retrieve SSO info for.
   * @returns {Promise<SsoInfoResponse>} A promise resolving to the SSO information.
   */
  getSsoInfo = async (s) => {
    const { data: t } = await this.get(
      `${a}/${s}/sso_info`
    );
    return t;
  };
  /**
   * Get business languages for an organization.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/languages`
   *
   * @param {number} orgId - The ID of the organization to retrieve languages for.
   * @param {OrgLanguagesRequest} params - Optional parameters for the request.
   * @returns {Promise<OrgLanguagesResponse>} A promise resolving to the organization languages data.
   */
  getBusinessesLanguages = async (s, t = {}) => {
    const { data: e } = await this.get(
      `${a}/${s}/languages`,
      t
    );
    return e;
  };
  /**
   * Get sidebar pages for an organization.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/sidebar_pages`
   *
   * @param {number} orgId - The ID of the organization to retrieve sidebar pages for.
   * @returns {Promise<SidebarPages>} A promise resolving to the sidebar pages data.
   */
  getSidebarPages = async (s) => {
    const { data: t } = await this.get(
      `${a}/${s}/sidebar_pages`
    );
    return t;
  };
  /**
   * Check business store codes for an organization.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/org/:orgId/store-codes`
   *
   * @param {number} orgId - The ID of the organization to check store codes for.
   * @param {string[]} storeCodes - Array of store codes to check for existence.
   * @returns {Promise<{ existing_store_codes: string[] }>} A promise resolving to the list of existing store codes.
   */
  checkBusinessStoreCode = async (s, t) => {
    const { data: e } = await this.post(
      `${a}/${s}/store-codes`,
      { store_codes: t }
    );
    return e;
  };
  /**
   * Get booking links configuration for an organization.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/booking-links/configuration`
   *
   * @param {number} orgId - The ID of the organization to retrieve booking links configuration for.
   * @returns {Promise<OrganizationDomainsGetDataType>} A promise resolving to the booking links configuration data.
   */
  getBookingLinksConfig = async (s) => {
    const { data: t } = await this.get(
      `${a}/${s}/booking-links/configuration`
    );
    return t;
  };
  /**
   * Tells if an organization is a "bulk verif" whitelisted org
   * This endpoint should be temporary.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/org/:orgId/bulk-whitelisted`
   *
   * @param {number} orgId - The ID of the organization to check.
   * @returns {Promise<boolean>} A boolean promise that tells whether the business belongs to a whitelisted org.
   */
  getIsInWhitelistedOrg = async (s) => {
    const { data: t } = await this.get(
      `${a}/${s}/bulk-whitelisted`
    );
    return t;
  };
}
export {
  a as ORGANIZATION_RESOURCE_URI,
  c as Organization
};
